import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Box, Typography, Skeleton, useTheme, useMediaQuery } from "@mui/material";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import octopusInstance from "../../services/octopus/octopusInstance";
import { fetchAllIndices, searchInstrument } from "../../services/backendAPI";
import { findNearestFuture } from "../Dashboard/utils/expiryUtils";
import { getExchangeCode } from "../../services/exchanges";
import { useNavigate } from "react-router-dom";

// Short expiry like "19 Jun" for futures contracts.
const shortExpiry = (unixSecs) => {
  if (!unixSecs) return "";
  const d = new Date(Number(unixSecs) * 1000);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", timeZone: "UTC" });
};

export const TOP_TICKER_HEIGHT = 34;
export const TOP_TICKER_HEIGHT_MOBILE = 30;

// Major NSE indices always shown in the ticker (NSE index exchange code = 1).
// These are guaranteed/prepended so they appear even if the indices API differs.
const TOP_NSE_INDICES = [
  { token: 26000, label: "NIFTY 50" },
  { token: 26009, label: "BANK NIFTY" },
  { token: 26037, label: "FINNIFTY" },
  { token: 26074, label: "MIDCPNIFTY" },
  { token: 26013, label: "NIFTY NXT 50" },
];

// Top MCX commodities whose nearest-expiry future is shown in the ticker.
const TOP_MCX = [
  { key: "CRUDEOIL", label: "CRUDE OIL" },
  { key: "NATURALGAS", label: "NATURAL GAS" },
  { key: "GOLD", label: "GOLD" },
  { key: "SILVER", label: "SILVER" },
  { key: "COPPER", label: "COPPER" },
  { key: "ZINC", label: "ZINC" },
];

const exchangeCodeFor = (name) => {
  try {
    return getExchangeCode(name);
  } catch {
    return 4; // MCX fallback
  }
};

// Memoized so only items whose values actually changed re-render — and with
// fixed-width numeric cells + tabular figures so changing digits never reflow
// the moving track (the main source of marquee jitter).
// Shimmer placeholder shown while the instrument list is loading or before
// the socket delivers the first tick for an item.
const TickerSkeleton = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 2,
        borderRight: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Skeleton animation="wave" width={64} height={12} />
      <Skeleton animation="wave" width={48} height={12} />
      <Skeleton animation="wave" width={40} height={12} />
    </Box>
  );
};

const TickerItem = React.memo(
  ({ label, expiry, ltp, pct, loading, exchange, token, onClick }) => {
    const theme = useTheme();
    const p = Number(pct ?? 0);
    const up = p > 0;
    const down = p < 0;
    const color = up
      ? theme.palette.success.main
      : down
      ? theme.palette.error.main
      : theme.palette.text.secondary;

    const handleClick = useCallback(() => {
      if (onClick) {
        onClick(exchange, token);
      }
    }, [onClick, exchange, token]);

    return (
      <Box
        onClick={handleClick}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          px: 2,
          borderRight: `1px solid ${theme.palette.divider}`,
          cursor: "pointer",
          transition: "background-color 0.2s ease",
          "&:hover": {
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.08)' 
              : 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <Box sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "text.primary" }}>
            {label}
          </Typography>
          {expiry && (
            <Typography variant="caption" sx={{ fontSize: "0.6rem", color: "text.secondary" }}>
              {expiry}
            </Typography>
          )}
        </Box>

        {loading ? (
          // Socket not connected / no tick yet → shimmer in place of the value.
          <Skeleton animation="wave" width={96} height={12} />
        ) : (
          <>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: "text.primary",
                minWidth: 56,
                textAlign: "right",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {Number(ltp ?? 0).toFixed(2)}
            </Typography>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "flex-end",
                width: 84,
                color,
              }}
            >
              <Box sx={{ width: 16, display: "inline-flex", justifyContent: "center" }}>
                {up ? (
                  <ArrowDropUpIcon sx={{ fontSize: "1rem" }} />
                ) : down ? (
                  <ArrowDropDownIcon sx={{ fontSize: "1rem" }} />
                ) : null}
              </Box>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}
              >
                {p > 0 ? "+" : ""}
                {p.toFixed(2)}%
              </Typography>
            </Box>
          </>
        )}
      </Box>
    );
  },
  (prev, next) =>
    prev.label === next.label &&
    prev.expiry === next.expiry &&
    prev.ltp === next.ltp &&
    prev.pct === next.pct &&
    prev.loading === next.loading &&
    prev.exchange === next.exchange &&
    prev.token === next.token &&
    prev.onClick === next.onClick
);

const TopTicker = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [items, setItems] = useState([]); // { id, token, exchange, exchangeCode, label }
  const [live, setLive] = useState({}); // token -> { ltp, change, pct }
  const liveRef = useRef({}); // latest values, flushed to state on a steady cadence
  const signatureRef = useRef(""); // token signature of the current item list

  // Build the instrument list: all top indices + nearest MCX futures.
  // findNearestFuture() only keeps a contract while its expiry is still in the
  // future, so this naturally picks the next contract once the front month
  // expires — provided we re-run it (see the scheduling effect below).
  const load = useCallback(async () => {
    const collected = [];
    const seenTokens = new Set();

    const addIndex = ({ token, exchange = "NSE", exchangeCode, label }) => {
      if (!token || seenTokens.has(token)) return;
      seenTokens.add(token);
      collected.push({
        id: `idx-${token}`,
        token,
        exchange,
        exchangeCode: exchangeCode || (exchange === "NSE" ? 1 : 2),
        label,
      });
    };

    // Always include the major NSE indices first.
    TOP_NSE_INDICES.forEach((idx) =>
      addIndex({ token: idx.token, exchange: "NSE", exchangeCode: 1, label: idx.label })
    );

    // Then merge in the rest from the indices API (skipping duplicates).
    // try {
    //   const res = await fetchAllIndices();
    //   const list = res?.data || res?.result || [];
    //   list.forEach((idx) => {
    //     if (!idx?.token) return;
    //     addIndex({
    //       token: idx.token,
    //       exchange: idx.exchange || "NSE",
    //       exchangeCode: idx.exchange_code || (idx.exchange === "NSE" ? 1 : 2),
    //       label: idx.trading_symbol || idx.symbol || idx.name || "INDEX",
    //     });
    //   });
    // } catch (err) {
    //   console.error("TopTicker: failed to fetch indices", err);
    // }

    // Top MCX futures (nearest non-expired contract).
    await Promise.all(
      TOP_MCX.map(async (c) => {
        try {
          const res = await searchInstrument(c.key);
          const results = Array.isArray(res) ? res : res?.result || res?.data || [];
          const fut = findNearestFuture(results, c.key);
          // Skip already-expired contracts (don't fall back to a stale one).
          const now = Math.trunc(Date.now() / 1000);
          if (fut?.token && Number(fut.expiry) > now) {
            collected.push({
              id: `mcx-${fut.token}`,
              token: fut.token,
              exchange: fut.exchange || "MCX",
              exchangeCode: exchangeCodeFor(fut.exchange || "MCX"),
              label: c.label,
              expiry: shortExpiry(fut.expiry),
            });
          }
        } catch (err) {
          console.error("TopTicker: failed to fetch MCX future", c.key, err);
        }
      })
    );

    // Only update (and re-subscribe sockets) when the contract set actually
    // changed — e.g. a future rolled to the next month.
    const signature = collected.map((c) => c.id).join("|");
    if (signature !== signatureRef.current) {
      signatureRef.current = signature;
      setItems(collected);
    }
  }, []);

  // Run on mount, then re-run hourly and whenever the tab regains focus, so an
  // expired future rolls over to the next contract without a manual reload.
  useEffect(() => {
    load();
    const interval = setInterval(load, 60 * 60 * 1000);
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);

  // 2. Live LTP / % change subscriptions.
  useEffect(() => {
    if (!items.length) return;

    const handlers = items.map((it) => {
      const handler = octopusInstance.wsHandler({
        messageType: "CompactMarketDataMessage",
        subscriptionLocation: `top-ticker-${it.token}`,
        payload: { exchangeCode: it.exchangeCode, instrumentToken: it.token },
      });
      if (handler) {
        handler.subscribe(({ msg }) => {
          if (!msg) return;
          const prev = liveRef.current[it.token];
          liveRef.current[it.token] = {
            ltp: msg.lastTradedPrice ?? msg.ltp ?? prev?.ltp ?? 0,
            change: msg.absoluteChange ?? prev?.change ?? 0,
            pct: msg.percentChange ?? prev?.pct ?? 0,
          };
        });
      }
      return handler;
    });

    // Flush accumulated socket updates at a steady 1s cadence instead of on
    // every tick — this stops the re-render storm that makes the marquee stutter.
    const flush = setInterval(() => setLive({ ...liveRef.current }), 1000);

    return () => {
      clearInterval(flush);
      handlers.forEach((h) => h && h.unsubscribe());
    };
  }, [items]);

  const navigate = useNavigate();

  const handleItemClick = useCallback((exchange, token) => {
    if (exchange && token) {
      navigate(`/overview/tdv/${exchange}/${token}`);
    }
  }, [navigate]);

  const tickerItems = useMemo(
    () => items.map((it) => ({ ...it, ...live[it.token] })),
    [items, live]
  );

  const ready = tickerItems.length > 0;
  // Duplicate the list so the marquee can loop seamlessly (translateX -50%).
  const loopItems = ready ? [...tickerItems, ...tickerItems] : [];
  const duration = Math.max(tickerItems.length * 5, 30);
  const height = isMobile ? TOP_TICKER_HEIGHT_MOBILE : TOP_TICKER_HEIGHT;
  const top = isMobile ? 56 : 64;

  return (
    <Box
      sx={{
        position: "fixed",
        top,
        left: 0,
        right: 0,
        height,
        zIndex: theme.zIndex.appBar - 1,
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        "&:hover .top-ticker-track": { animationPlayState: "paused" },
      }}
    >
      {ready ? (
        <Box
          className="top-ticker-track"
          sx={{
            display: "inline-flex",
            flexShrink: 0,
            alignItems: "center",
            whiteSpace: "nowrap",
            willChange: "transform",
            animation: `top-ticker-scroll ${duration}s linear infinite`,
            "@keyframes top-ticker-scroll": {
              "0%": { transform: "translateX(0)" },
              "100%": { transform: "translateX(-50%)" },
            },
          }}
        >
          {loopItems.map((it, i) => (
            <TickerItem
              key={`${it.id}-${i}`}
              label={it.label}
              expiry={it.expiry}
              ltp={it.ltp}
              pct={it.pct}
              loading={it.ltp === undefined}
              exchange={it.exchange}
              token={it.token}
              onClick={handleItemClick}
            />
          ))}
        </Box>
      ) : (
        // Instrument list still loading → shimmer placeholders.
        <Box sx={{ display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}>
          {Array.from({ length: 10 }).map((_, i) => (
            <TickerSkeleton key={`sk-${i}`} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default TopTicker;
